import type { ReceiptKit } from './types/receipt';

enum BILLING_ADDRESS {
  SHIPPING = 0,
  CUSTOM = 1,
  SAVED = 2,
  SAVED_CUSTOM = 3,
}
export enum BillingMethods {
  CREDIT_CARD = 'credit_card',
  FREE = 'free',
  PAYPAL = 'paypal',
  APPLEPAY = 'applepay',
  VENMO = 'venmo',
  GOOGLEPAY = 'googlepay',
  SAVED_PAYMENT = 'saved_payment',
}
enum API_URLS {
  PAYMENTS = '/en-us/payments/',
  FAMILY_TREE = '/en-us/family_tree/',
  SAVE_PAYMENT = '/payments/save/',
  SUBSCRIPTION_PAYMENTS = '/subscribe/',
  RENEW_SUBSCRIPTION_PAYMENTS = '/renew-subscribe/',
  SUBSCRIPTION_RENEWAL_OPTIN = '/renew-optin/',
  HEALTH_UPGRADE_UPDATE_HUMAN = '/kits/update_human/',
  FEATURE_EVENT = '/feature_event/',
  CANCELLATION_FOMO_DATA = '/cancellation_fomo_data/',
  FEATURE_SWITCH = '/feature_switch/',
  ADDRESS_AUTOCOMPLETE_SESSION = '/2/address_autocomplete/',
}

export type FeatureSwitchIds = 'show_cancellation_flow_v2';
// Be sure to also add new ids to product:/busobjects/account.py::BOHumanFeatureEvent::FEATURES
export type FeatureIds = 'subscription_cancellation_flow';
export type FeatureEvent = 'started' | 'completed';

enum STEP {
  ADDRESS = 1,
  VERIFICATION = 2,
  SHIP_METHOD = 3,
  BILLING = 4,
  REVIEW = 5,
}

const ANIMATION_CLASSNAME = 'mod-anim';
const PREPAID_SUBSCRIPTION_KIT_ALIAS = 'prepaid_subscription_kit';
const PREPAID_AOS_KIT_ALIAS = 'prepaid_aos_kit';
const CUSTOM_ADDRESS_SOURCES = [BILLING_ADDRESS.SAVED_CUSTOM, BILLING_ADDRESS.CUSTOM];

const ALABAMA = 'AL';
const AMERICAN_SAMOA = 'AS';
const ARMED_FORCES_AFRICA = 'AF';
const ARMED_FORCES_AMERICAS = 'AA';
const ARMED_FORCES_CANADA = 'AC';
const ARMED_FORCES_EUROPE = 'AE';
const ARMED_FORCES_MIDDLE_EAST = 'AM';
const ARMED_FORCES_PACIFIC = 'AP';
const GEORGIA = 'GA';
const GUAM = 'GU';
const HAWAII = 'HI';
const LOUISIANA = 'LA';
const MARSHALL_ISLANDS = 'MH';
const MISSISSIPPI = 'MS';
const NORTHERN_MARIANA_ISLANDS = 'MP';
const NEW_JERSEY = 'NJ';
const NEW_YORK = 'NY';
const PUERTO_RICO = 'PR';
const RHODE_ISLAND = 'RI';
const SOUTH_CAROLINA = 'SC';
const TENNESSEE = 'TN';
const VIRGIN_ISLANDS = 'VI';
const UNITED_STATES = 'US';

export const DISALLOWED_STATES_FOR_TOTAL_HEALTH_KITS = [
  ALABAMA,
  AMERICAN_SAMOA,
  ARMED_FORCES_AFRICA,
  ARMED_FORCES_AMERICAS,
  ARMED_FORCES_CANADA,
  ARMED_FORCES_EUROPE,
  ARMED_FORCES_MIDDLE_EAST,
  ARMED_FORCES_PACIFIC,
  GEORGIA,
  GUAM,
  HAWAII,
  LOUISIANA,
  MARSHALL_ISLANDS,
  MISSISSIPPI,
  NORTHERN_MARIANA_ISLANDS,
  NEW_JERSEY,
  NEW_YORK,
  PUERTO_RICO,
  RHODE_ISLAND,
  SOUTH_CAROLINA,
  TENNESSEE,
  VIRGIN_ISLANDS,
];

export const PA_LABEL = 'Premium Ancestry';
export const PA_HEALTH_LABEL = 'Premium Ancestry + Health';
export const PA_TOTAL_HEALTH_LABEL = 'Premium Ancestry + Total Health';
// BKO-5025: what a bare "23andMe+" in prose becomes — the umbrella word, not a tier name.
export const GENERIC_PREMIUM_LABEL = 'Premium';

// BKO-5355: read by both surfaces stating a pending cart mutation, so they cannot drift.
export const CART_UPDATING_MESSAGE = 'Updating your cart';

// BKO-5248: one receipt disclaimer per prepaid membership type. A 1-month kit shares its
// tier's 12-month alias, so it is excluded from both; alias-less legacy payloads fall back to
// the PAH disclaimer.
type OneMonthReceiptKit = Extract<ReceiptKit, { is_one_month_prepaid: true }>;

const isOneMonthReceiptKit = (kit: ReceiptKit): kit is OneMonthReceiptKit =>
  kit.is_one_month_prepaid;

type OneMonthPrepaidKit = Kit & {
  isOneMonthPrepaid: true;
  membershipName: string;
  serviceName: string;
};

export const isOneMonthPrepaidKit = (kit: Kit): kit is OneMonthPrepaidKit =>
  kit.isOneMonthPrepaid === true &&
  typeof kit.membershipName === 'string' &&
  typeof kit.serviceName === 'string';

export const getPrepaidDisclaimerFlags = (kits: ReceiptKit[], isTotalHealth: boolean) => {
  const trialKit = kits.find(isOneMonthReceiptKit);
  const annualKits = kits.filter((kit) => !kit.is_one_month_prepaid);
  const hasAosKit = annualKits.some((kit) => kit.alias === PREPAID_AOS_KIT_ALIAS);
  const hasPahKit =
    annualKits.some((kit) => kit.alias === PREPAID_SUBSCRIPTION_KIT_ALIAS) ||
    (!trialKit && !hasAosKit && !isTotalHealth);
  return { trialKit, hasAosKit, hasPahKit };
};

export {
  API_URLS,
  ANIMATION_CLASSNAME,
  STEP,
  PREPAID_SUBSCRIPTION_KIT_ALIAS,
  PREPAID_AOS_KIT_ALIAS,
  CUSTOM_ADDRESS_SOURCES,
  PUERTO_RICO,
  UNITED_STATES,
};
